import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, MapPin, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/register', formData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Registrasi berhasil!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registrasi gagal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-md flex items-center justify-center">
              <span className="text-black font-black text-2xl">Z</span>
            </div>
            <span className="text-3xl font-heading font-black italic text-primary uppercase">Zero Futsal</span>
          </Link>
          <h1 className="text-3xl font-heading font-bold uppercase text-white mb-2">Daftar</h1>
          <p className="text-zinc-400">Buat akun baru Anda</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Nama Lengkap</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  data-testid="register-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 pl-12 pr-4 placeholder:text-zinc-600 transition-all outline-none"
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  data-testid="register-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 pl-12 pr-4 placeholder:text-zinc-600 transition-all outline-none"
                  placeholder="nama@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  data-testid="register-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 pl-12 pr-4 placeholder:text-zinc-600 transition-all outline-none"
                  placeholder="Min. 6 karakter"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">No. Handphone</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  data-testid="register-phone-input"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md h-12 pl-12 pr-4 placeholder:text-zinc-600 transition-all outline-none"
                  placeholder="08123456789"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">Alamat</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-4 w-5 h-5 text-zinc-500" />
                <textarea
                  data-testid="register-address-input"
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary text-white rounded-md pl-12 pr-4 py-3 placeholder:text-zinc-600 transition-all outline-none resize-none"
                  placeholder="Jl. Contoh No. 123"
                  rows={3}
                />
              </div>
            </div>

            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-8 py-3 font-bold uppercase tracking-wider transition-all duration-200 shadow-[4px_4px_0px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_0px_#FFFFFF] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span>{loading ? 'Memproses...' : 'Daftar'}</span>
              {!loading && <UserPlus className="w-5 h-5" />}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400">
              Sudah punya akun?{' '}
              <Link to="/login" className="text-primary hover:underline font-semibold">
                Masuk sekarang
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;